(() => {
  const reportHeading = /\b(test(?:ed|ing| results?)?|results?|lab(?:oratory)?|analys(?:is|es|tical)|what (?:we|the report) found|original report|specimen findings)\b/i;
  const reportImage = /\b(report page|analytical report|diffraction|spectrum|micrograph|figure\s*\d+)\b/i;
  const normalize = value => value.toLocaleLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

  function collectionFigures(body) {
    return Array.from(body.querySelectorAll('figure')).filter(figure => {
      if (figure.closest('.sbg-tested__report-grid, .sbg-african__reports, details')) return false;
      const image = figure.querySelector('img');
      return image && !reportImage.test(image.alt || '');
    });
  }

  function firstReportIndex(nodes) {
    const originalReport = nodes.findIndex(node => node.classList?.contains('sbg-tested__report-heading'));
    if (originalReport >= 0) return originalReport;
    return nodes.findIndex(node => {
      if (node.classList?.contains('sbg-tested__fact')) return true;
      return /^H[2-4]$/.test(node.tagName) && reportHeading.test(node.textContent || '');
    });
  }

  function materialTerms(title) {
    const stripped = title.replace(/\b(?:gem knowledge|laboratory notes|material profile|tested material)\b/ig, '');
    return normalize(stripped).split(/\s+/).filter(term =>
      term.length > 2 && !['and', 'the', 'from', 'with', 'for'].includes(term)
    );
  }

  function productCard(product) {
    const link = document.createElement('a');
    const productUrl = new URL(product.url || '', location.origin);
    if (productUrl.origin !== location.origin || !/(^|\/)products\//.test(productUrl.pathname)) return null;
    link.className = 'sbg-tested__product';
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) productUrl.searchParams.set('preview_theme_id', preview);
    link.href = productUrl.pathname + productUrl.search;
    const imageUrl = product.featured_image?.url || product.image;
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = product.featured_image?.alt || product.title || '';
      image.loading = 'lazy';
      image.width = 260;
      image.height = 260;
      link.append(image);
    }
    const name = document.createElement('span');
    name.className = 'sbg-tested__product-title';
    name.textContent = product.title || 'View item';
    link.append(name);
    const price = Number(product.price);
    const currency = window.Shopify?.currency?.active;
    if (Number.isFinite(price) && currency) {
      const amount = document.createElement('span');
      try {
        amount.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(price);
        link.append(amount);
      } catch (_) {}
    }
    return link;
  }


  function materialMatch(title) {
    const value = normalize(title);
    const families = [
      ['chrysocolla', 'chrysocolla', ['chrysocolla']],
      ['malachite', 'malachite', ['malachite']],
      ['blue lace agate', 'blue lace agate', ['blue lace agate']],
      ['ocean jasper', 'ocean jasper', ['ocean jasper', 'spirit jasper']],
      ['snakeskin jasper', 'snakeskin jasper', ['snakeskin jasper']],
      ['ibis jasper', 'ibis jasper', ['ibis jasper']],
      ['rhodochrosite', 'rhodochrosite', ['rhodochrosite']],
      ['owyhee', 'opal', ['owyhee', 'blue opal']],
      ['blue opal', 'opal', ['owyhee', 'blue opal']],
      ['larimar', 'larimar', ['larimar']],
      ['trolleite', 'trolleite', ['trolleite']],
      ['lepidolite', 'lepidolite', ['lepidolite']],
      ['howardite', 'howardite', ['howardite']],
      ['herkimer', 'herkimer', ['herkimer']],
      ['amethyst', 'amethyst', ['amethyst']],
      ['citrine', 'citrine', ['citrine']],
      ['fluorite', 'fluorite', ['fluorite']],
      ['pyrite', 'pyrite', ['pyrite']],
      ['turquoise', 'turquoise', ['turquoise']],
      ['plum blossom', 'plum blossom', ['plum blossom']],
      ['jadeite', 'jadeite', ['jadeite']],
      ['obsidian', 'obsidian', ['obsidian']],
      ['carnelian', 'carnelian', ['carnelian']],
      ['agate', 'agate', ['agate']],
      ['ammonite', 'ammonite', ['ammonite', 'orthocera']],
      ['orthocera', 'orthocera', ['orthocera', 'ammonite']],
      ['phurba', 'phurba', ['phurba']],
      ['emerald', 'emerald', ['emerald']],
      ['sapphire', 'sapphire', ['sapphire']],
      ['spinel', 'spinel', ['spinel']],
      ['tourmaline', 'tourmaline', ['tourmaline']],
      ['garnet', 'garnet', ['garnet']],
      ['kyanite', 'kyanite', ['kyanite']],
      ['aquamarine', 'aquamarine', ['aquamarine']],
      ['tanzanite', 'tanzanite', ['tanzanite']],
      ['amber', 'amber', ['amber']],
      ['sunstone', 'sunstone', ['sunstone']],
      ['quartz', 'quartz', ['quartz']],
      ['jasper', 'jasper', ['jasper']],
      ['opal', 'opal', ['opal']]
    ];
    for (const [name, query, matches] of families) {
      if (value.includes(name)) return { query, matches };
    }
    return null;
  }

  async function addAvailableProducts(body, title, before) {
    const family = materialMatch(title);
    if (!family || family.query.length < 3) return;
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(root + 'search/suggest.json', location.origin);
    url.searchParams.set('q', family.query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok || !body.isConnected) return;
      const data = await response.json();
      const products = (data.resources?.results?.products || []).filter(product => {
        const name = normalize(product.title || '');
        return product.available !== false && family.matches.some(term => name.includes(term));
      }).slice(0, 4);
      if (!products.length || body.querySelector('.sbg-gem-layout__shop')) return;
      const section = document.createElement('section');
      section.className = 'sbg-tested__related sbg-gem-layout__shop';
      section.setAttribute('aria-label', 'Similar material for sale at Soul Body Gems');
      const heading = document.createElement('h2');
      heading.textContent = 'Similar material for sale at Soul Body Gems';
      const note = document.createElement('p');
      note.className = 'sbg-gem-layout__shop-note';
      note.textContent = 'Explore related pieces currently available. These are not necessarily the specimens documented above; see each listing for its identification, origin and testing information.';
      const grid = document.createElement('div');
      grid.className = 'sbg-tested__products';
      products.forEach(product => {
        const card = productCard(product);
        if (card) grid.append(card);
      });
      if (!grid.childElementCount) return;
      section.append(heading, note, grid);
      body.insertBefore(section, before);
    } catch (_) {}
  }

  function arrange(body, title, curatedProducts) {
    if (!body || body.dataset.gemLayoutReady === 'true') return;
    const figures = collectionFigures(body);
    figures.forEach(figure => figure.remove());
    const nodes = Array.from(body.childNodes);
    const groups = { background: [], science: [], metaphysical: [], sources: [] };
    let topic = 'background';
    let introParagraphs = 0;
    const headingTopic = text => {
      if (/metaphysical|spiritual|chakra|symbolism|meaning|healing|\benergy\b/i.test(text)) return 'metaphysical';
      if (/sources|references|further reading|bibliography/i.test(text)) return 'sources';
      if (/background|introduction|overview|history|story behind/i.test(text)) return 'background';
      return 'science';
    };
    nodes.forEach(node => {
      const explicit = node.dataset?.gemTopic;
      if (explicit && groups[explicit]) {
        groups[explicit].push(node);
        return;
      }
      if (/^H[2-4]$/.test(node.tagName)) topic = headingTopic(node.textContent || '');
      else if (node.classList?.contains('sbg-tested__fact') || node.classList?.contains('sbg-tested__report-grid') || node.classList?.contains('sbg-tested__transcript')) topic = 'science';
      else if (topic === 'background' && node.tagName === 'P') {
        if (introParagraphs >= 2 || /laboratory|\bXRD\b|\bSEM\b|not (?:been )?tested|sample \d+/i.test(node.textContent)) topic = 'science';
        else introParagraphs++;
      }
      groups[topic].push(node);
    });
    const story = document.createElement('div');
    story.className = 'sbg-gem-layout__story';
    const imageOnly = /^(?:Blue Tara)$/i.test(title.trim()) || !nodes.some(node => node.textContent?.trim());
    if (!imageOnly) {
      document.title = title + ' Meaning, Metaphysical Energy & Geology | Soul Body Gems';
      const labels = { background: 'Background', science: 'Science / Origin / Testing', metaphysical: 'Metaphysical Meaning', sources: 'Sources and further reading' };
      for (const key of ['background', 'science', 'metaphysical', 'sources']) {
        const content = groups[key];
        if (key === 'sources' && !content.some(node => node.textContent?.trim())) continue;
        const section = document.createElement('section');
        section.className = 'sbg-gem-layout__topic sbg-gem-layout__' + key;
        section.dataset.gemTopic = key;
        const heading = document.createElement('h2');
        heading.textContent = labels[key];
        section.append(heading);
        if (key === 'science' || key === 'metaphysical') {
          const subheading = document.createElement('h3');
          subheading.textContent = title + (key === 'science' ? ' geology, origin and testing' : ' meaning and energy');
          section.append(subheading);
        }
        const hasContent = content.some(node => node.textContent?.trim());
        content.forEach(node => {
          if (node.dataset?.gemTopic === key) {
            Array.from(node.childNodes).forEach(child => {
              if (/^H[2-4]$/.test(child.tagName) && child.textContent.trim() === labels[key]) child.remove();
              else section.append(child);
            });
          } else if (/^H[2-4]$/.test(node.tagName) && node.textContent.trim() === labels[key]) node.remove();
          else section.append(node);
        });
        if (!hasContent) {
          const note = document.createElement('p');
          note.textContent = key === 'background'
            ? 'Explore ' + title + ' through our collection photographs and the information below.'
            : key === 'science'
              ? 'Geology, origin and testing information for this material will be added as it becomes available.'
              : 'Metaphysical meaning and energy are matters of spiritual tradition and personal interpretation, separate from geology and laboratory testing.';
          section.append(note);
        }
        story.append(section);
      }
    } else nodes.forEach(node => story.append(node));
    const intro = document.createElement('div');
    intro.className = 'sbg-gem-layout__intro';
    if (figures.length > 1) {
      body.classList.add('sbg-gem-layout--multi');
      const gallery = document.createElement('aside');
      gallery.className = 'sbg-gem-layout__gallery';
      gallery.setAttribute('aria-label', title + ' collection photographs');
      figures.forEach(figure => gallery.append(figure));
      intro.append(story, gallery);
    } else if (figures.length === 1) {
      body.classList.add('sbg-gem-layout--single');
      const hero = document.createElement('div');
      hero.className = 'sbg-gem-layout__hero';
      hero.append(figures[0]);
      intro.append(hero, story);
    } else intro.append(story);
    body.replaceChildren(intro);
    if (curatedProducts && curatedProducts.querySelector('.sbg-tested__product')) {
      curatedProducts.classList.add('sbg-gem-layout__shop');
      body.append(curatedProducts);
    } else if (curatedProducts) curatedProducts.remove();
    body.dataset.gemLayoutReady = 'true';
    if (!curatedProducts?.querySelector('.sbg-tested__product')) addAvailableProducts(body, title, null);
  }

  function start() {
    const native = document.querySelector('.sbg-gem-detail__copy');
    if (native) arrange(native, document.querySelector('.sbg-gem-detail h1')?.textContent || document.title,
      document.querySelector('.sbg-gem-detail__related'));
    const tested = document.querySelector('.sbg-tested:not(.sbg-tested--preview) > .sbg-tested__body');
    if (tested) arrange(tested, document.querySelector('.sbg-tested:not(.sbg-tested--preview) h1')?.textContent || document.title,
      document.querySelector('.sbg-tested:not(.sbg-tested--preview) > .sbg-tested__related'));
    const reportView = document.querySelector('#sbg-report-view:not([hidden]) #sbg-report-body');
    if (reportView) arrange(reportView, document.querySelector('#sbg-report-title')?.textContent || document.title, null);
    const african = document.querySelector('.sbg-african');
    if (african) {
      const body = african.querySelector('.sbg-african__body');
      const hero = african.querySelector(':scope > .sbg-african__hero');
      if (body && hero) body.prepend(hero);
      arrange(body, african.querySelector('h1')?.textContent || document.title,
        african.querySelector('.sbg-african__related'));
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

(() => {
  const scope = '.sbg-gem-page__card, .sbg-gem-detail, .sbg-tested, .sbg-african';
  const excluded = '.sbg-tested__report-grid, .sbg-african__reports, .sbg-tested__dialog, .sbg-tested__product, .sbg-gem-layout__shop, [data-photo-credit-external], [data-sbg-credit="external"]';
  const positionedImages = new WeakSet();
  let creditFrame = 0;
  function positionCredit(image) {
    const frame = image.closest('.sbg-gem-photo');
    if (!frame || !image.naturalWidth || !image.naturalHeight) return;
    const box = image.getBoundingClientRect();
    const outer = frame.getBoundingClientRect();
    if (!box.width || !box.height) return;
    const style = getComputedStyle(image);
    let insetX = 0, insetY = 0;
    if (style.objectFit === 'contain' || style.objectFit === 'scale-down') {
      const scale = Math.min(box.width / image.naturalWidth, box.height / image.naturalHeight, style.objectFit === 'scale-down' ? 1 : Infinity);
      const position = style.objectPosition.split(' ');
      const fraction = (value, fallback) => value?.endsWith('%') ? parseFloat(value) / 100 : fallback;
      insetX = Math.max(0, box.width - image.naturalWidth * scale) * fraction(position[0], 0.5);
      insetY = Math.max(0, box.height - image.naturalHeight * scale) * fraction(position[1], 0.5);
      frame.style.setProperty('--sbg-credit-right', Math.max(6, outer.right - box.right + insetX + 8) + 'px');
      frame.style.setProperty('--sbg-credit-bottom', Math.max(6, outer.bottom - box.bottom + insetY + 8) + 'px');
    } else {
      frame.style.setProperty('--sbg-credit-right', Math.max(6, outer.right - box.right + 8) + 'px');
      frame.style.setProperty('--sbg-credit-bottom', Math.max(6, outer.bottom - box.bottom + 8) + 'px');
    }
  }
  function positionAllCredits() {
    creditFrame = 0;
    document.querySelectorAll('.sbg-gem-photo>img').forEach(positionCredit);
  }
  function scheduleCreditPosition() {
    if (!creditFrame) creditFrame = requestAnimationFrame(positionAllCredits);
  }
  const photoResize = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleCreditPosition) : null;
  window.addEventListener('resize', scheduleCreditPosition, { passive: true });
  function trackCredit(image) {
    if (!positionedImages.has(image)) {
      positionedImages.add(image);
      image.addEventListener('load', scheduleCreditPosition);
      photoResize?.observe(image);
      photoResize?.observe(image.closest('.sbg-gem-photo'));
    }
    positionCredit(image);
  }
  function creditPhotos() {
    document.querySelectorAll(scope.split(', ').map(part => part + ' img').join(', ')).forEach(image => {
      if (image.closest(excluded) || image.closest('.sbg-gem-photo')) return;
      const source = image.getAttribute('src') || image.getAttribute('data-src') || '';
      if (!/\/s\/files\/1\/0713\/7581\/4847\/files\/sbg-/i.test(source)) return;
      if (/report page|analytical report|diffraction|spectrum|micrograph|figure\s*\d+/i.test(image.alt || '')) return;
      if (/(?:report|xrd|spectrum|micrograph|scan)[-_]/i.test(source.split('/files/')[1] || '')) return;
      const figure = image.closest('figure');
      const caption = figure?.querySelector('figcaption')?.textContent || '';
      if (/courtesy|wikimedia|wikipedia|photo(?:graph)? (?:credit|by)|image (?:credit|source)/i.test(caption) && !/Soul Body Gems/i.test(caption)) return;
      const parent = image.parentElement;
      const previous = Array.from(parent.children).find(node => node.tagName === 'SPAN' && /^(?:©|@)\s*(?:Soul\s*Body\s*Gems|soulbodygems)\.?$/i.test(node.textContent.trim()));
      let frame;
      if (previous) {
        frame = parent;
        previous.classList.add('sbg-gem-photo__credit');
        previous.textContent = '© Soul Body Gems';
        previous.setAttribute('aria-hidden', 'true');
      } else {
        frame = document.createElement('span');
        image.before(frame);
        frame.append(image);
        const credit = document.createElement('span');
        credit.className = 'sbg-gem-photo__credit';
        credit.textContent = '© Soul Body Gems';
        credit.setAttribute('aria-hidden', 'true');
        frame.append(credit);
      }
      frame.classList.add('sbg-gem-photo');
      trackCredit(image);
    });
  }
  function initializePhotoCredits() {
    creditPhotos();
    let scheduled = false;
    new MutationObserver(records => {
      if (scheduled || !records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches('img') || node.querySelector('img'))))) return;
      scheduled = true;
      queueMicrotask(() => { scheduled = false; creditPhotos(); });
    }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializePhotoCredits, { once: true });
  else initializePhotoCredits();
})();
